import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@istari-frontend/ui";
import { useEffect, useId, useRef, useState } from "react";

import type { InfosecLevelOption } from "~/ai-chat/ui/chat-save-resource-card";
import { cn } from "~/utils/cn";
import { formatInfosecLevelName } from "~/utils/infosec";

interface ChatSaveTranscriptDialogProps {
  open: boolean;
  defaultName: string;
  // undefined = the platform infosec setting hasn't resolved yet; saving stays
  // blocked so an infosec-required deployment can't slip through the gap.
  infosecEnabled: boolean | undefined;
  infosecLevels: InfosecLevelOption[];
  isSaving: boolean;
  onSave: (name: string, infosecLevelId?: string) => void;
  onCancel: () => void;
}

export function ChatSaveTranscriptDialog({
  open,
  defaultName,
  infosecEnabled,
  infosecLevels,
  isSaving,
  onSave,
  onCancel,
}: ChatSaveTranscriptDialogProps) {
  const nameInputId = useId();
  const infosecLevelSelectId = useId();
  const [name, setName] = useState(defaultName);
  const [infosecLevelId, setInfosecLevelId] = useState<string | null>(null);

  // Reset only on the closed→open edge — not on every `defaultName` change, which
  // is derived from the streaming transcript and would clobber a name the user is
  // editing while the dialog is open.
  const prevOpenRef = useRef(open);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setName(defaultName);
      setInfosecLevelId(null);
    }
    prevOpenRef.current = open;
  }, [open, defaultName]);

  const trimmedName = name.trim();
  const isMissingInfosecLevel = infosecEnabled === true && infosecLevelId === null;
  const canSave = trimmedName.length > 0 && infosecEnabled !== undefined && !isMissingInfosecLevel && !isSaving;

  const handleSave = () => {
    if (!canSave) return;
    onSave(trimmedName, infosecLevelId ?? undefined);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !isSaving && onCancel()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Save chat to your library</DialogTitle>
          <DialogDescription>The transcript is saved as a new resource in your library.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={nameInputId} className="text-xs">
              Name
            </Label>
            <Input
              id={nameInputId}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Istari chat"
            />
          </div>
          {infosecEnabled && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={infosecLevelSelectId} className="text-xs">
                Infosec level <span className="text-destructive">*</span>
              </Label>
              <Select value={infosecLevelId ?? ""} onValueChange={(value) => setInfosecLevelId(value || null)}>
                <SelectTrigger
                  id={infosecLevelSelectId}
                  className={cn(isMissingInfosecLevel && "border-destructive ring-destructive/20")}
                  aria-invalid={isMissingInfosecLevel}
                >
                  <SelectValue placeholder="Select level…" />
                </SelectTrigger>
                <SelectContent>
                  {infosecLevels.map((level) => (
                    <SelectItem key={level.id} value={level.id}>
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full" style={{ backgroundColor: level.displayColor }} />
                        {formatInfosecLevelName(level.name)}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={!canSave}>
            {isSaving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
